/**
 * AnswerButtonOverUnder - Specialized answer button for over/under questions
 *
 * Renders Over/Under options with percentage stat integrated inside the button.
 * Layout: [percentage stat | label | arrow] with flex, items-center, justify-between.
 */

import React from 'react';
import { OptionPercentageStat } from '../OptionPercentageStat';
import { useQuestionCardStyle } from '../../contexts/QuestionCardStyleContext';
import { pickFlourishClass, PickSweep, usePickInteraction } from '../pick-flourish';

interface AnswerButtonOverUnderProps {
  answer: 'over' | 'under';
  label: string;
  isSelected: boolean;
  /**
   * Whether EITHER answer on this question is selected — see AnswerButton. Defaults to
   * `isSelected` so callers that don't pass it keep the chosen option's lift without the
   * loser receding.
   */
  anySelected?: boolean;

  /**
   * Whether the QUESTION has been graded. Callers pass `isCorrect` only to the option the
   * player chose, so the other button cannot tell a graded question from a live one and
   * would otherwise keep offering pick feedback on a settled question. Defaults to
   * whether this button itself has a result.
   */
  isGraded?: boolean;
  percentage?: number;
  onClick: () => void;
  disabled?: boolean;
  isCorrect?: boolean;
  testId?: string;
  isDailyDouble?: boolean;
  className?: string;
  /** Off for a yes/no question, where an up or down arrow on "Yes" means nothing. */
  showArrow?: boolean;
}

const ARROW_UP = '↑';
const ARROW_DOWN = '↓';

export const AnswerButtonOverUnder: React.FC<AnswerButtonOverUnderProps> = ({
  answer,
  label,
  isSelected,
  anySelected,
  isGraded,
  percentage,
  onClick,
  disabled = false,
  isCorrect,
  testId,
  isDailyDouble = false,
  className = '',
  showArrow = true,
}) => {
  const { ddButtonBg, ddButtonBorder } = useQuestionCardStyle();
  const hasResult = isCorrect !== undefined;
  const questionGraded = isGraded ?? hasResult;
  const { hasInteracted, interactionProps, arm } = usePickInteraction();
  const resultBorderClass =
    isSelected && hasResult
      ? isCorrect
        ? 'border-2 border-green-500 shadow-[0_0_15px_4px_rgba(34,197,94,0.4)]'
        : 'border-2 border-red-500 shadow-[0_0_15px_4px_rgba(239,68,68,0.4)]'
      : '';

  const getBorderClass = () => {
    if (hasResult) return resultBorderClass;
    if (isDailyDouble) {
      if (isSelected) return 'border border-white';
      return 'border border-[#0D54AD]';
    }
    if (isSelected) return 'border border-[#6F6F6F]';
    return 'border border-[#121212]';
  };

  const getHoverClass = () => {
    if (disabled || hasResult) return '';
    return isDailyDouble ? 'hover:border-white' : 'hover:border-[#6F6F6F]';
  };

  const getBackgroundClass = () => {
    if (isSelected) return 'bg-white';
    if (isDailyDouble) return 'bg-[#000D1F]';
    return 'bg-[#242424]';
  };

  const arrowIcon = showArrow ? (answer === 'over' ? ARROW_UP : ARROW_DOWN) : null;

  const ddInlineStyle: React.CSSProperties =
    isDailyDouble && !isSelected && !hasResult
      ? {
          ...(ddButtonBg ? { backgroundColor: ddButtonBg } : {}),
          ...(ddButtonBorder ? { borderColor: ddButtonBorder } : {}),
        }
      : {};

  return (
    <button
      data-testid={testId || `over-under-answer-${answer}`}
      data-test-result={isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
      data-selected={isSelected ? 'true' : 'false'}
      {...interactionProps}
      onClick={() => {
        arm();
        onClick();
      }}
      disabled={disabled}
      style={ddInlineStyle}
      className={`
        group peer w-full md:p-4 p-2 rounded-[4px] transition-smooth overflow-hidden
        relative flex items-center justify-center md:justify-between
        ${answer === 'over' ? 'rounded-tr-[8px]' : 'rounded-br-[8px]'}
        ${getBackgroundClass()}
        ${getBorderClass()}
        ${getHoverClass()}
        ${pickFlourishClass({ isSelected, anySelected: anySelected ?? isSelected, hasResult: questionGraded, disabled, animate: hasInteracted })}
        ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
        ${className}
      `}
    >
      {/* Left: Percentage stat — absolutely positioned top-left on small, in flow on md+ */}
      <div className="absolute top-0 left-1 md:static md:top-auto md:left-auto min-w-[min-content]">
        {percentage !== undefined ? (
          <OptionPercentageStat percentage={percentage} isSelected={isSelected} allowResponsiveFlexDirection={true} />
        ) : null}
      </div>

      {/* Small screens: Label and Arrow grouped and centered */}
      <div className="flex items-center justify-center gap-2 md:hidden">
        <div
          className={`font-bold text-lg ${
            isSelected ? 'text-black' : 'text-text-primary'
          }`}
        >
          {label}
        </div>
        <div
          className={`min-w-[24px] flex items-center justify-center font-bold text-lg ${
            isSelected ? 'text-black' : 'text-text-primary'
          }`}
        >
          {arrowIcon}
        </div>
      </div>

      {/* md+ screens: Label and Arrow separate for space-between layout */}
      <div
        className={`hidden md:flex flex-1 justify-center font-bold text-lg ${
          isSelected ? 'text-black' : 'text-text-primary'
        }`}
      >
        {label}
      </div>
      <div
        className={`hidden md:flex flex-shrink-0 min-w-[24px] items-center md:justify-end font-bold text-lg ${
          isSelected ? 'text-black' : 'text-text-primary'
        }`}
      >
        {arrowIcon}
      </div>

      {!questionGraded && <PickSweep active={isSelected} animate={hasInteracted} />}
    </button>
  );
};
